import { LayoutItem, ResponsiveLayoutValue } from '../types';

/**
 * Compensate for gaps between and at the edges of panels in a row.
 *
 * ## Requirements & Behavior
 *
 * This function is designed to improve dashboard layout aesthetics by expanding panels horizontally into empty space
 * when the dashboard is not in editing mode. It operates on a single row at a time, taking into account all panels
 * that overlap that row (panels may have different heights).
 *
 * ### 1. Between-panel gaps
 *   - For each gap between two adjacent panels, the function attempts to split the gap as evenly as possible between them.
 *   - If the gap is an odd number of columns
 *     - The extra column is given to the wider panel.
 *     - If both are equal width, the extra goes to the left panel.
 *   - Panels are only expanded into the gap if the space is empty for the full height of the panel (no overlap with other panels).
 *
 * ### 2. Edge compensation
 *   - If the first panel in a row does not start at the left edge (x > 0), it will expand left to fill the empty space, provided the space is empty for its full height.
 *   - If the last panel in a row does not reach the right edge (x + w < columns), it will expand right to fill the empty space, provided the space is empty for its full height.
 *   - If a single panel is present in a row with gaps on both sides, it can expand both left and right to fill the row.
 *
 * @param panelsInRow Array of panels overlapping the current row, sorted by x
 * @param panelMap Map of panel id to panel object (not mutated)
 * @param columns Number of columns in the grid
 * @returns A new Map with compensated panel positions and widths
 */
export function compensateRowGaps(
    panelsInRow: ReactGridLayout.Layout[],
    panelMap: Map<string, ReactGridLayout.Layout>,
    columns: number
): Map<string, ReactGridLayout.Layout> {
    // Work on a copy
    const newPanelMap = new Map(panelMap);

    // 1. Try to compensate for gaps between panels
    // For each gap between panels
    for (let i = 0; i < panelsInRow.length - 1; i += 1) {
        // Get the two panels to check
        const leftItem = newPanelMap.get(panelsInRow[i].i);
        const rightItem = newPanelMap.get(panelsInRow[i + 1].i);

        // eslint-disable-next-line no-continue -- This is intentional, we don't need to compensate for gaps if there are no panels
        if (!leftItem || !rightItem) continue;

        // Copy the panels to avoid mutating the original
        const left = { ...leftItem };
        const right = { ...rightItem };

        // Get the gap between the two panels
        const gap = right.x - (left.x + left.w);

        // Check if there is a gap between the two panels
        if (gap > 0) {
            // Split the gap as evenly as possible between the two panels
            const half = Math.floor(gap / 2);
            const odd = gap % 2;
            let leftExpand = half;
            let rightExpand = half;
            // Decide who gets the odd column, left panel if they are the same width,
            // or left panel if it's the first panel in the row
            if (left.w > right.w || (left.w === right.w && i === 0)) {
                leftExpand += odd;
            } else {
                rightExpand += odd;
            }

            // Check for overlap if we expand

            // A. Expand left panel
            let canExpandLeft = true;
            // Check each column that the left panel would expand into
            for (let dx = 1; dx <= leftExpand; dx += 1) {
                // Check each row that the left panel would expand into
                for (let dy = 0; dy < (left.h || 1); dy += 1) {
                    // Get the column that the left panel would expand into
                    const col = left.x + left.w + dx - 1;
                    // Get the row that the left panel would expand into
                    const y = left.y + dy;
                    // Check if the column and row is occupied by another panel
                    if (
                        Array.from(newPanelMap.values()).some(
                            (p) =>
                                p.i !== left.i &&
                                y >= p.y &&
                                y < p.y + (p.h || 1) &&
                                col >= p.x &&
                                col < p.x + p.w
                        )
                    ) {
                        canExpandLeft = false;
                        break;
                    }
                }
                if (!canExpandLeft) break;
            }

            // B. Expand right panel
            let canExpandRight = true;
            // Check each column that the right panel would expand into
            for (let dx = 1; dx <= rightExpand; dx += 1) {
                // Check each row that the right panel would expand into
                for (let dy = 0; dy < (right.h || 1); dy += 1) {
                    // Get the column that the right panel would expand into
                    const col = right.x - dx;
                    // Get the row that the right panel would expand into
                    const y = right.y + dy;
                    // Check if the column and row is occupied by another panel
                    if (
                        Array.from(newPanelMap.values()).some(
                            (p) =>
                                p.i !== right.i &&
                                y >= p.y &&
                                y < p.y + (p.h || 1) &&
                                col >= p.x &&
                                col < p.x + p.w
                        )
                    ) {
                        canExpandRight = false;
                        break;
                    }
                }
                if (!canExpandRight) break;
            }
            // Apply expansion if possible
            if (canExpandLeft && leftExpand > 0) {
                left.w += leftExpand;
            }
            if (canExpandRight && rightExpand > 0) {
                right.x -= rightExpand;
                right.w += rightExpand;
            }

            // Update the map
            newPanelMap.set(left.i, { ...left });
            newPanelMap.set(right.i, { ...right });
        }
    }

    // 2. Edge compensation
    if (panelsInRow.length > 0) {
        // A. Left edge
        const firstItem = newPanelMap.get(panelsInRow[0].i);
        if (firstItem) {
            // Copy the panel to avoid mutating the original
            const first = { ...firstItem };
            // Check if the panel is not already at the left edge
            if (first.x > 0) {
                // Check each column that the panel would expand into
                let canExpandLeft = true;
                for (let col = 0; col < first.x; col += 1) {
                    for (let dy = 0; dy < (first.h || 1); dy += 1) {
                        // Get the row that the panel would expand into
                        const y = first.y + dy;
                        // Check if the column and row is occupied by another panel
                        if (
                            Array.from(newPanelMap.values()).some(
                                (p) =>
                                    p.i !== first.i &&
                                    y >= p.y &&
                                    y < p.y + (p.h || 1) &&
                                    col >= p.x &&
                                    col < p.x + p.w
                            )
                        ) {
                            canExpandLeft = false;
                            break;
                        }
                    }
                    if (!canExpandLeft) break;
                }
                // Apply expansion if possible
                if (canExpandLeft) {
                    first.w += first.x;
                    first.x = 0;
                    newPanelMap.set(first.i, { ...first });
                }
            }
        }
        // B. Right edge
        const lastItem = newPanelMap.get(panelsInRow[panelsInRow.length - 1].i);
        if (lastItem) {
            // Copy the panel to avoid mutating the original
            const last = { ...lastItem };
            // Check if the panel is not already at the right edge
            const rightGap = columns - (last.x + last.w);
            // Check if there is a gap between the panel and the right edge
            if (rightGap > 0) {
                // Check if the panel can expand right
                let canExpandRight = true;
                for (let col = last.x + last.w; col < columns; col += 1) {
                    for (let dy = 0; dy < (last.h || 1); dy += 1) {
                        // Get the row that the panel would expand into
                        const y = last.y + dy;
                        // Check if the column and row is occupied by another panel
                        if (
                            Array.from(newPanelMap.values()).some(
                                (p) =>
                                    p.i !== last.i &&
                                    y >= p.y &&
                                    y < p.y + (p.h || 1) &&
                                    col >= p.x &&
                                    col < p.x + p.w
                            )
                        ) {
                            canExpandRight = false;
                            break;
                        }
                    }
                    if (!canExpandRight) break;
                }
                // Apply expansion if possible
                if (canExpandRight) {
                    last.w += rightGap;
                    newPanelMap.set(last.i, { ...last });
                }
            }
        }
    }

    // Return the new panel map
    return newPanelMap;
}

interface CustomLayout extends Omit<ReactGridLayout.Layout, 'h'> {
    h: number | ResponsiveLayoutValue;
}

/**
 * Scale a layout.
 *
 * ## Requirements & Behavior
 *
 * This function scales a layout to a new number of columns, compensating
 * the panels width and horizontal position.
 *
 * @param layout The layout to scale.
 * @param columns The number of columns in the layout.
 * @param breakpoint The breakpoint to scale the layout to.
 * @returns The scaled layout.
 */
export function scaleLayout(
    layout: LayoutItem[],
    columns: number,
    breakpoint: number
): CustomLayout[] {
    /** The number of columns for which the layout is optimized. */
    const OriginalColumns = 12;
    /** The minimal height of a panel. */
    const minRowsHeight = 2;
    /** The minimal width of a panel in columns. */
    const minColsWidth = 1;
    /** The minimal width of a panel in pixels. */
    const minPixelsWidth = 256;

    const colWidth = breakpoint / columns;
    const minW = Math.min(Math.round(minPixelsWidth / colWidth), minColsWidth);
    const minH = minRowsHeight;

    return layout.map((item) => {
        // Scale the panel's horizontal position
        const newX = Math.round((item.x / OriginalColumns) * columns);
        // Scale the panel's width
        let newW = Math.max(
            Math.round((item.w / OriginalColumns) * columns),
            minColsWidth
        );
        // If the panel's horizontal position plus its width is greater than the number of
        // columns, set the width to the number of columns minus the horizontal position
        if (newX + newW > columns) {
            newW = Math.max(columns - newX, minColsWidth);
        }
        return { ...item, minH, minW, w: newW, x: newX };
    });
}

/** The number of columns in the layout for each breakpoint. */
const DEFAULT_COLUMNS = { lg: 12, md: 10, sm: 6, xs: 2, xxs: 1 };
/** The order of breakpoints to try when no exact match is found. */
const FALLBACK_ORDER: (keyof ResponsiveLayoutValue)[] = [
    'lg',
    'md',
    'sm',
    'xs',
    'xxs'
];

/**
 * Get the value of a layout item for a given breakpoint.
 *
 * @param value The value of the layout item.
 * @param breakpoint The breakpoint to get the value for.
 * @returns The value of the layout item for the given breakpoint.
 */
export function valueFromBreakpoint(
    value: number | ResponsiveLayoutValue,
    breakpoint: keyof ResponsiveLayoutValue
): number {
    if (typeof value === 'object') {
        // If object is empty, throw an error
        if (Object.keys(value).length === 0) {
            throw new Error('Layout value cannot be empty');
        }

        // If breakpoint is not a valid key, throw an error
        if (!FALLBACK_ORDER.includes(breakpoint)) {
            throw new Error(`Invalid breakpoint: ${breakpoint}`);
        }

        // Try exact match first
        if (breakpoint in value) {
            return value[breakpoint] as number;
        }

        // Locating the current breakpoint in the fallback order
        const currentIndex = FALLBACK_ORDER.indexOf(breakpoint);

        // 1. Try to match a smaller breakpoint
        for (let i = currentIndex; i < FALLBACK_ORDER.length; i += 1) {
            if (FALLBACK_ORDER[i] in value) {
                return value[FALLBACK_ORDER[i]] as number;
            }
        }

        // 2. Try to match a larger breakpoint
        for (let i = currentIndex; i >= 0; i -= 1) {
            if (FALLBACK_ORDER[i] in value) {
                return value[FALLBACK_ORDER[i]] as number;
            }
        }
    }

    return value as number;
}

/**
 * Scale a masonry layout.
 *
 * ## Requirements & Behavior
 *
 * This function scales a masonry layout to a new number of columns, compensating
 * for gaps between and at the edges of panels.
 *
 * ### 1. Scaling
 *   - The layout is scaled to the new number of columns.
 *   - The layout is scaled to the new number of rows.
 *   - The layout is scaled to the new number of columns and rows.
 *
 * ### 2. Compensating for gaps
 *   - The layout is compensated for gaps between and at the edges of panels.
 *
 *
 * @param layout The layout to scale.
 * @param isEditing Whether the layout is in editing mode.
 */
export function scaleMasonryLayout({
    layout,
    shouldAdjust,
    columns = DEFAULT_COLUMNS
}: {
    /** The layout to scale. */
    layout: LayoutItem[];
    /** Whether to adjust the layout. */
    shouldAdjust: boolean;
    /** The number of columns in the layout for each breakpoint. */
    columns?: Record<string, number>;
}): {
    /** The breakpoints used in the layout. */
    breakpoints: Record<string, number>;
    /** The number of columns in the layout for each breakpoint. */
    columns: Record<string, number>;
    /** The layouts for each breakpoint. */
    layouts: ReactGridLayout.Layouts;
} {
    /** The breakpoints used in the layout. In pixels, from largest to smallest. */
    const breakpoints = { lg: 1200, md: 900, sm: 600, xs: 480, xxs: 0 };

    const layouts: ReactGridLayout.Layouts = {};

    if (!shouldAdjust) {
        Object.keys(columns).forEach((breakpoint) => {
            layouts[breakpoint] = layout.map((item) => ({
                ...item,
                h: valueFromBreakpoint(
                    item.h,
                    breakpoint as keyof ResponsiveLayoutValue
                )
            }));
        });
        return { breakpoints, columns, layouts };
    }

    Object.entries(columns).forEach(([breakpoint, columns]) => {
        const scaled = scaleLayout(
            layout,
            columns,
            breakpoints[breakpoint as keyof typeof breakpoints]
        );

        let scaledWithHeight: ReactGridLayout.Layout[] = scaled.map((item) => ({
            ...item,
            h: valueFromBreakpoint(
                item.h,
                breakpoint as keyof ResponsiveLayoutValue
            )
        }));

        // Find the max Y (bottom row)
        const maxY = scaledWithHeight.reduce(
            (max, item) => Math.max(max, item.y + (item.h || 1)),
            0
        );
        // We'll work on a copy to avoid mutating the original array
        let panelMap = new Map<string, ReactGridLayout.Layout>(
            scaledWithHeight.map((item) => [item.i, { ...item }])
        );

        // For each row
        for (let row = 0; row < maxY; row += 1) {
            // Find all panels that overlap this row
            const panelsInRow = Array.from(panelMap.values()).filter(
                (item) => row >= item.y && row < item.y + (item.h || 1)
            );
            // Sort by x
            panelsInRow.sort((a, b) => a.x - b.x);
            // Compensate gaps and edges for this row, get new panelMap
            panelMap = compensateRowGaps(panelsInRow, panelMap, columns);
        }
        // Use the updated panels for this breakpoint
        scaledWithHeight = Array.from(panelMap.values());

        layouts[breakpoint] = scaledWithHeight;
    });

    return { breakpoints, columns, layouts };
}
